import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AuthDavesaV2Service } from '../auth-davesa/auth-davesa-v2.service';
import { NotificationsV2Service } from '../../layout/common/notifications/notifications-v2.service';

export interface NotificationAction {
  id: string;
  icon: string;
  color: 'primary' | 'warn' | 'accent';
  tooltip: string;
  isOneTimeAction: boolean; // true: accept, decline, archive, delete; false: view, review, open, reply
  actionCompleted?: boolean; // Only for isOneTimeAction=true, driven by notification.action_completed
}

@Injectable({
  providedIn: 'root'
})
export class NotificationActionsService {
  private _authService = inject(AuthDavesaV2Service);
  private _router = inject(Router);
  private _snackBar = inject(MatSnackBar);
  private _notificationsService = inject(NotificationsV2Service);

  /**
   * Declare available actions for a notification category
   * Pure declaration - no filtering logic, just defines what actions exist
   */
  getAvailableActions(notification: any): NotificationAction[] {
    const categoryCode = notification.notification_category?.code;
    // No actions by default
    return [];
  }

  /**
   * Execute an action - handles the business logic for each action type
   */
  async executeAction(actionId: string, notification: any): Promise<{ 
    success: boolean; 
    message?: string; 
    shouldMarkAsRead?: boolean;
  }> {
    const categoryCode = notification.notification_category?.code;

    try {
      // Default error if unknown action
      return { 
        success: false, 
        message: 'This action is not available'
      };
    } catch (error: any) {
      console.error('Action execution error:', error);
      
      // Extract friendly error message from response
      const friendlyMessage = this._extractFriendlyErrorMessage(error);
      
      return {
        success: false,
        message: friendlyMessage
      };
    }
  }

  /**
   * Mark a one-time action as completed (for failed actions)
   */
  async markActionAsCompleted(notification: any): Promise<{ success: boolean; message?: string }> {
    try {
      const updateData = {
        oid: notification.oid,
        action_completed: true,
        has_read: true
      };
      
      await this._notificationsService.updateNotificationItem(updateData);
      
      return {
        success: true,
        message: 'Action marked as completed'
      };
    } catch (error: any) {
      console.error('Error marking action as completed:', error);
      
      // Extract friendly error message from response
      const friendlyMessage = this._extractFriendlyErrorMessage(error);
      
      return {
        success: false,
        message: friendlyMessage
      };
    }
  }

  /**
   * Extract friendly error message from HTTP error response
   */
  private _extractFriendlyErrorMessage(error: any): string {
    // Try to get error message from response body first
    let errorMessage = '';
    
    // Check for error in response body (most common)
    if (error?.error?.error) {
      errorMessage = error.error.error;
    }
    // Check for message in response body
    else if (error?.error?.message) {
      errorMessage = error.error.message;
    }
    // Check for direct message
    else if (error?.message) {
      errorMessage = error.message;
    }
    
    // If we found an error message, make it user-friendly
    if (errorMessage) {
      return errorMessage;
    }
    
    // Fallback based on HTTP status code
    const statusCode = error?.status || error?.error?.status;
    return this._getFallbackErrorMessage(statusCode);
  }

  /**
   * Get fallback error message based on HTTP status code
   */
  private _getFallbackErrorMessage(statusCode?: number): string {
    switch (statusCode) {
      case 400:
        return 'The request is invalid. Please try again.';
      case 401:
        return 'Your session has expired. Please log in again.';
      case 403:
        return 'You do not have permission to perform this action.';
      case 404:
        return 'This notification is no longer available.';
      case 409:
        return 'This action has already been completed.';
      case 422:
        return 'The provided information is not valid. Please check and try again.';
      case 500:
      case 502:
      case 503:
        return 'Server is temporarily unavailable. Please try again in a few moments.';
      case 504:
        return 'Request timed out. Please try again.';
      default:
        return 'Unable to complete this action. Please try again or contact support.';
    }
  }

}